import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { RevealText, FadeUp } from "@/components/site/Reveal";
import { IMAGES } from "@/data/images";

const CategoryPage = ({ tKey, imageKey = "macro_eagle" }) => {
  const { t, lang } = useLang();
  const data = t[tKey];

  return (
    <div data-testid={`category-page-${tKey}`} className="pt-28 md:pt-36">
      <section className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-6">{t.services.eyebrow}</div>
            <RevealText lines={[data.title]} as="h1" className={`display text-white ${lang === "ur" ? "text-lg sm:text-xl md:text-2xl lg:text-3xl" : lang === "en" ? "text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05]" : "text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.92]"}`} />
            <FadeUp delay={0.2}>
              <p className={`mt-10 text-white/70 max-w-3xl leading-relaxed ${lang === "ur" ? "text-base" : "text-lg md:text-xl"}`}>{data.body}</p>
            </FadeUp>
          </div>
          <FadeUp delay={0.1} className="md:col-span-4">
            <div className="spot-image rounded-md overflow-hidden aspect-[4/5]">
              <img src={IMAGES[imageKey]} alt="" className="w-full h-full object-cover grayscale-[25%] contrast-125" />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-32">
        <ul className="space-y-2">
          {data.items.map((s, i) => (
            <FadeUp key={i} delay={i * 0.04}>
              <li className="group flex items-baseline gap-6 md:gap-10 border-b border-white/10 py-5 md:py-6 hover:border-[color:var(--sw-orange)] transition-colors">
                <span className="chapter-num text-sm md:text-base w-12">{String(i + 1).padStart(2, "0")}</span>
                <span className={`text-white/85 group-hover:text-white transition-colors ${lang === "ur" ? "text-lg" : "display text-2xl md:text-4xl"}`}>{s}</span>
                <ArrowUpRight className="ms-auto text-white/25 group-hover:text-[color:var(--sw-orange)] transition-colors" size={22} />
              </li>
            </FadeUp>
          ))}
        </ul>
      </section>

      {/* CTA strip */}
      <section className="border-t border-white/10">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-16 md:py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-3">{t.nav.contact}</div>
            <RevealText lines={[t.home.quickContact.title]} as="h3" className="display text-3xl md:text-4xl text-white" />
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary px-6 py-3 rounded-full inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em]" data-testid="cat-cta-request">{t.nav.requestService}</Link>
            <a href="https://wa.me/966590960314" target="_blank" rel="noreferrer" className="btn-ghost px-5 py-3 rounded-full inline-flex items-center gap-2" data-testid="cat-cta-whatsapp"><MessageCircle size={16}/> WhatsApp</a>
            <a href="tel:+966590960314" target="_blank" rel="noreferrer" className="btn-ghost px-5 py-3 rounded-full inline-flex items-center gap-2" data-testid="cat-cta-call"><Phone size={16}/> 0590960314</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CategoryPage;
